import {
  Comment as CommentIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import ParticipantsAvatars from "../ParticipantsAvatars";
import ComplaintClassificationChip from "../ComplaintClassificationChip";
import {
  getAvatarColor,
  getAvatarImageSrc,
  getAvatarInitials,
  hasProfilePicture,
} from "../../utils/avatarUtils";
import { formatDate } from "../../utils/dateFormatter";
import {
  getPriorityColor,
  getPriorityLabelKey,
  getTicketParticipants,
} from "../../utils/ticketParticipants";

const FieldLabel = ({ children }) => (
  <Typography
    component="span"
    variant="subtitle2"
    color="text.secondary"
    sx={{
      fontSize: "0.8125rem",
      fontWeight: 600,
      lineHeight: 1.4,
      display: "block",
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

const getUserDisplayName = (user) => {
  if (!user) return null;
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`.trim();
  }
  return user.username;
};

const UserAvatarCell = ({ user }) => {
  const { t } = useTranslation();
  if (!user) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        —
      </Typography>
    );
  }
  const displayName = getUserDisplayName(user);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mt: 0.25,
        minWidth: 0,
      }}
    >
      <Tooltip title={user.username}>
        <Avatar
          src={getAvatarImageSrc(user)}
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            bgcolor: hasProfilePicture(user) ? undefined : getAvatarColor(user),
          }}
        >
          {!hasProfilePicture(user) && getAvatarInitials(user)}
        </Avatar>
      </Tooltip>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {displayName || t("common.unknown")}
      </Typography>
    </Box>
  );
};

const TagRow = ({ items, idField, isRTL }) => {
  if (!items?.length) return null;
  const lang = isRTL ? "name_ar" : "name_en";
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {items.map((item) => (
        <Chip
          key={item[idField]}
          label={item[lang] || item.name_ar || item.name_en}
          size="small"
          variant="outlined"
        />
      ))}
    </Box>
  );
};

const TicketSearchCard = ({ ticket, isRTL, onNavigate, onQuickPreview }) => {
  const { t } = useTranslation();
  const creator = ticket.add_by_user_id;
  const allParticipants = getTicketParticipants(ticket);
  const creatorId = creator?.id ?? creator?.user_id;
  const replyParticipants = allParticipants.filter(
    (p) => p.user_id != null && String(p.user_id) !== String(creatorId)
  );
  const closedBy = ticket.closed_by_user_id;
  const isClosed = ticket.status === "closed";
  const priority = ticket.priority || "medium";

  const handleViewDetails = () => {
    if (onNavigate) {
      onNavigate(`/tickets/${ticket.ticket_id}`);
    }
  };

  const handlePreview = () => {
    onQuickPreview(ticket);
  };

  const statusLabel =
    ticket.status === "open"
      ? t("search.status.open")
      : t("search.status.closed");

  const commName = isRTL
    ? ticket.communication_method?.name_ar
    : ticket.communication_method?.name_en;
  const reasonName = isRTL
    ? ticket.contact_reason?.name_ar
    : ticket.contact_reason?.name_en;

  const hasTags =
    ticket.branches?.length > 0 ||
    ticket.products?.length > 0 ||
    ticket.departments?.length > 0;

  // xs: عمود واحد | sm/md: عمودان (تابلت) | lg+: 4 أعمدة
  const gridResponsiveCols = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, 1fr)",
      lg: "repeat(4, 1fr)",
    },
    gap: 2,
    alignItems: "start",
  };

  return (
    <Card
      dir={isRTL ? "rtl" : "ltr"}
      sx={{
        mb: 2,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 3 },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pt: 2, pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              label={`#${ticket.ticket_id}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={statusLabel}
              size="small"
              color={ticket.status === "open" ? "success" : "default"}
            />
            <Chip
              label={t(getPriorityLabelKey(priority))}
              size="small"
              color={getPriorityColor(priority)}
            />
            <ComplaintClassificationChip ticket={ticket} displayOnly />
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
            <Tooltip title={t("search.quickPreview")}>
              <IconButton size="small" onClick={handlePreview} color="primary">
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 0.5 }}>
              <CommentIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {ticket.reply_count ?? 0}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: 600,
            fontSize: "1.05rem",
            lineHeight: 1.35,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            mb: 1.5,
          }}
        >
          {ticket.title}
        </Typography>

        <Divider sx={{ borderColor: "divider", opacity: 0.9 }} />
      </Box>

      {/* Body */}
      <CardContent sx={{ pt: 2, pb: 1 }}>
        <Box sx={{ ...gridResponsiveCols, mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>{t("tickets.createdBy")}</FieldLabel>
            <UserAvatarCell user={creator} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>{t("home.tables.participants")}</FieldLabel>
            <Box sx={{ minHeight: 36, display: "flex", alignItems: "center" }}>
              {replyParticipants.length > 0 ? (
                <ParticipantsAvatars
                  participants={replyParticipants}
                  maxDisplay={5}
                  size="small"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("home.tables.noParticipants")}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>{t("search.customer")}</FieldLabel>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: { xs: "normal", md: "nowrap" },
              }}
            >
              {ticket.customer?.name || "—"}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>{t("ticketView.closedBy")}</FieldLabel>
            {isClosed && closedBy ? (
              <UserAvatarCell user={closedBy} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ ...gridResponsiveCols, mb: hasTags ? 2 : 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>{t("tickets.createdDate")}</FieldLabel>
            <Typography variant="body2">{formatDate(ticket.created_date)}</Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>{t("search.lastUpdated")}</FieldLabel>
            <Typography variant="body2">
              {formatDate(ticket.last_updated || ticket.created_date)}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>{t("tickets.communicationMethod")}</FieldLabel>
            <Typography variant="body2">{commName || "—"}</Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>{t("createTicket.contactReasonLabel")}</FieldLabel>
            <Typography variant="body2">{reasonName || "—"}</Typography>
          </Box>
        </Box>

        {hasTags && (
          <Box sx={{ ...gridResponsiveCols }}>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel>{t("ticketView.branches")}</FieldLabel>
              {ticket.branches?.length > 0 ? (
                <TagRow items={ticket.branches} idField="branch_id" isRTL={isRTL} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel>{t("ticketView.products")}</FieldLabel>
              {ticket.products?.length > 0 ? (
                <TagRow items={ticket.products} idField="product_id" isRTL={isRTL} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                minWidth: 0,
                gridColumn: { xs: "auto", sm: "span 2", lg: "span 2" },
              }}
            >
              <FieldLabel>{t("tickets.departments")}</FieldLabel>
              {ticket.departments?.length > 0 ? (
                <TagRow
                  items={ticket.departments}
                  idField="department_id"
                  isRTL={isRTL}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </CardContent>

      {/* Footer */}
      <Divider sx={{ borderColor: "divider", opacity: 0.9 }} />
      <CardActions
        sx={{
          px: 2,
          py: 1.25,
          justifyContent: "flex-end",
        }}
      >
        <Button variant="contained" size="small" onClick={handleViewDetails}>
          {t("tickets.viewDetails")}
        </Button>
      </CardActions>
    </Card>
  );
};

export default TicketSearchCard;
